// CRM-NANCOZI — Gestion documentaire (CNI, contrats, avenants…)
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FolderOpen, Upload, Download, Trash2, Search,
  FileText, FileBadge, FileArchive, Eye,
} from 'lucide-react';

const TYPE_LABELS = {
  cni: "CNI", passeport: "Passeport", fiche_id: "Fiche ID",
  contrat: "Contrat", avenant: "Avenant", autre: "Autre",
};
const TYPE_COLOR = {
  cni: '#1F4E79', passeport: '#2E75B6', fiche_id: '#1E7145',
  contrat: '#ED7D31', avenant: '#C00000', autre: '#6B7280',
};

// Constante hors composant pour éviter la recréation à chaque render
const FORM_INIT = { client: '', commentaire: '', typeFiles: {} };

export default function DocumentsPage() {
  const [docs,        setDocs]        = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filterTypes, setFilterTypes] = useState(() => new Set());
  const [showModal,   setShowModal]   = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [form,        setForm]        = useState(FORM_INIT);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients/documents/');
      setDocs(Array.isArray(data) ? data : data.results || []);
    } catch { toast.error('Erreur chargement documents.'); }
    finally { setLoading(false); }
  }, []); // stable — pas de dépendances externes

  useEffect(() => {
    fetchDocs();
    api.get('/clients/').then(r => setClients(Array.isArray(r.data) ? r.data : r.data.results || []));
  }, []); // [] — exécuté une seule fois au montage

  const toggleType = (key) => {
    setFilterTypes(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const filtered = docs.filter(d => {
    const q = search.toLowerCase();
    const okSearch = !search ||
      (d.nom_fichier || '').toLowerCase().includes(q) ||
      (d.client_nom  || '').toLowerCase().includes(q);
    const okType = filterTypes.size === 0 || filterTypes.has(d.type_document);
    return okSearch && okType;
  });

  // Cocher/décocher un type dans le formulaire
  const toggleFormType = (key) => {
    setForm(f => {
      const tf = { ...f.typeFiles };
      if (tf[key] !== undefined) delete tf[key]; else tf[key] = null;
      return { ...f, typeFiles: tf };
    });
  };

  // Attacher un fichier à un type coché
  const setTypeFile = (key, file) => {
    setForm(f => ({ ...f, typeFiles: { ...f.typeFiles, [key]: file } }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.client) { toast.error('Sélectionnez un client.'); return; }
    const entries = Object.entries(form.typeFiles).filter(([, f]) => f instanceof File);
    if (entries.length === 0) { toast.error('Cochez au moins un type et joignez un fichier.'); return; }
    setUploading(true);
    let ok = 0, ko = 0;
    for (const [type, fichier] of entries) {
      const fd = new FormData();
      fd.append('client', form.client);
      fd.append('type_document', type);
      fd.append('fichier', fichier);
      try {
        await api.post('/clients/documents/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch { ko++; }
    }
    setUploading(false);
    if (ok > 0) { toast.success(`${ok} document(s) ajouté(s) avec succès.`); fetchDocs(); }
    if (ko > 0)   toast.error(`${ko} document(s) en échec.`);
    if (ok > 0) { setShowModal(false); setForm(FORM_INIT); }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer le document "${nom}" ?`)) return;
    try {
      await api.delete(`/clients/documents/${id}/`);
      toast.success('Document supprimé.');
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch { toast.error('Erreur lors de la suppression.'); }
  };

  const fmt     = d  => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const fmtSize = ko => ko >= 1024 ? `${(ko / 1024).toFixed(1)} Mo` : `${ko} Ko`;

  return (
    <div className="page-list">
      {/* ── Barre d'actions ── */}
      <div style={{ marginBottom: 12 }}>
        {/* Ligne 1 : recherche + bouton */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div className="search-bar" style={{ flex: '1 1 200px', minWidth: 200 }}>
            <Search size={14} />
            <input placeholder="Nom fichier, client…" value={search}
              onChange={e => setSearch(e.target.value)} style={{ fontSize: 12 }} />
          </div>
          <button className="header-btn btn-primary"
            onClick={() => setShowModal(true)}
            style={{ height: 36, padding: '0 16px', fontSize: 13, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Upload size={15} /> Ajouter un document
          </button>
        </div>

        {/* Ligne 2 : filtre par type (cases à cocher) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '6px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8 }}>
          <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, flexShrink: 0 }}>Filtrer :</span>
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
              fontSize: 12, fontWeight: filterTypes.has(key) ? 700 : 400,
              color: filterTypes.has(key) ? TYPE_COLOR[key] : '#6B7280', userSelect: 'none' }}>
              <input type="checkbox" checked={filterTypes.has(key)} onChange={() => toggleType(key)}
                style={{ accentColor: TYPE_COLOR[key], cursor: 'pointer', width: 14, height: 14 }} />
              {label}
            </label>
          ))}
          {filterTypes.size > 0 && (
            <button onClick={() => setFilterTypes(new Set())}
              style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 4 }}>
              Tout afficher
            </button>
          )}
        </div>
      </div>

      {/* ── KPI cards ── */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Total documents', value: docs.length,                                             icon: FolderOpen,  color: '#1F4E79' },
            { label: 'Contrats',        value: docs.filter(d=>d.type_document==='contrat').length,      icon: FileText,    color: '#ED7D31' },
            { label: 'CNI / Passeports',value: docs.filter(d=>['cni','passeport'].includes(d.type_document)).length, icon: FileBadge, color: '#1E7145' },
            { label: 'Avenants',        value: docs.filter(d=>d.type_document==='avenant').length,      icon: FileArchive, color: '#C00000' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: color + '18', borderRadius: 8, padding: 8, flexShrink: 0 }}>
                <Icon size={18} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280' }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', lineHeight: 1.2 }}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tableau ── */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><FolderOpen size={36} /><p>Aucun document trouvé</p></div>
        ) : (
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '23%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: 90   }} />
              <col style={{ width: 60   }} />
              <col style={{ width: 75   }} />
              <col style={{ width: 90   }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: 80   }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ fontSize: 11 }}>Nom du fichier</th>
                <th style={{ fontSize: 11 }}>Client</th>
                <th style={{ fontSize: 11 }}>Type</th>
                <th style={{ fontSize: 11 }}>Version</th>
                <th style={{ fontSize: 11 }}>Taille</th>
                <th style={{ fontSize: 11 }}>Date upload</th>
                <th style={{ fontSize: 11 }}>Chargé par</th>
                <th style={{ fontSize: 11 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id}>
                  <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FileText size={13} color={TYPE_COLOR[d.type_document] || '#6B7280'} style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={d.nom_fichier}>{d.nom_fichier}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {d.client_nom || '—'}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                      color: TYPE_COLOR[d.type_document] || '#6B7280',
                      background: (TYPE_COLOR[d.type_document] || '#6B7280') + '18',
                    }}>
                      {TYPE_LABELS[d.type_document] || d.type_document}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#1F4E79' }}>
                    v{d.version_doc}
                  </td>
                  <td style={{ fontSize: 11, color: '#6B7280', padding: '8px 6px' }}>
                    {fmtSize(d.taille_ko)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px' }}>{fmt(d.date_upload)}</td>
                  <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {d.uploade_par_nom || '—'}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {d.url_fichier && (
                        <a href={d.url_fichier} target="_blank" rel="noreferrer"
                          className="header-btn btn-secondary btn-sm btn-icon" title="Voir / Télécharger"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                          <Eye size={13} />
                        </a>
                      )}
                      {d.url_fichier && (
                        <a href={d.url_fichier} download
                          className="header-btn btn-secondary btn-sm btn-icon" title="Télécharger"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                          <Download size={13} />
                        </a>
                      )}
                      <button className="header-btn btn-danger btn-sm btn-icon"
                        onClick={() => handleDelete(d.id, d.nom_fichier)} title="Supprimer">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal upload ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload size={16} /> Ajouter un document
              </h2>
              <button className="header-btn btn-secondary btn-sm btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleUpload}>
              <div className="modal-body">
                {/* Client */}
                <div className="form-group">
                  <label className="form-label required">Client</label>
                  <select className="form-control" value={form.client}
                    onChange={e => setForm(f => ({ ...f, client: e.target.value }))} required>
                    <option value="">— Sélectionnez un client —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.prenom_cli} {c.nom_cli}</option>
                    ))}
                  </select>
                </div>

                {/* Types de documents avec cases à cocher + fichier par type */}
                <div className="form-group">
                  <label className="form-label required">
                    Types de documents <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF' }}>
                      (cochez et joignez un fichier par type)
                    </span>
                  </label>
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                    {Object.entries(TYPE_LABELS).map(([key, label], i) => {
                      const isChecked = key in form.typeFiles;
                      const hasFile   = form.typeFiles[key] instanceof File;
                      return (
                        <div key={key} style={{
                          borderTop: i > 0 ? '1px solid #F3F4F6' : 'none',
                          background: isChecked ? (TYPE_COLOR[key] + '08') : 'white',
                          transition: 'background 0.15s',
                        }}>
                          {/* Ligne case à cocher */}
                          <label style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 14px', cursor: 'pointer', userSelect: 'none',
                          }}>
                            <input type="checkbox" checked={isChecked}
                              onChange={() => toggleFormType(key)}
                              style={{ accentColor: TYPE_COLOR[key], width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                            <span style={{
                              fontSize: 13, fontWeight: isChecked ? 700 : 400,
                              color: isChecked ? TYPE_COLOR[key] : '#374151',
                            }}>
                              {label}
                            </span>
                            {isChecked && hasFile && (
                              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#1E7145', fontWeight: 600 }}>
                                ✓ {form.typeFiles[key].name}
                              </span>
                            )}
                            {isChecked && !hasFile && (
                              <span style={{ marginLeft: 'auto', fontSize: 10, color: '#ED7D31' }}>
                                Fichier requis
                              </span>
                            )}
                          </label>
                          {/* Input fichier — visible seulement si coché */}
                          {isChecked && (
                            <div style={{ padding: '0 14px 10px 40px' }}>
                              <input type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                style={{ fontSize: 12, width: '100%' }}
                                onChange={e => setTypeFile(key, e.target.files[0] || null)} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <small style={{ color: '#9CA3AF', fontSize: 11, marginTop: 4, display: 'block' }}>
                    PDF, images, Word acceptés. Le versioning est automatique par type.
                  </small>
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="header-btn btn-secondary"
                  onClick={() => { setShowModal(false); setForm(FORM_INIT); }}>Annuler</button>
                <button type="submit" className="header-btn btn-primary" disabled={uploading}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Upload size={14} />
                  {uploading ? 'Envoi en cours…'
                    : `Uploader ${Object.values(form.typeFiles).filter(f => f instanceof File).length || ''} document(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
